import { Link, useParams } from "react-router-dom";
import { useMemo } from "react";
import { ArrowLeft, Calendar, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useGetVisitorByIdQuery } from "@/api/visitorApi";
import { useGetDashboardOrdersQuery } from "@/api/dashboardApi";
import { formatUsDateTime } from "@/utils/date";
import { useSelector } from "react-redux";
import type { RootState } from "@/store";

function formatDateTime(value?: string | null) {
  return formatUsDateTime(value);
}

export default function VisitorOrdersPage() {
  const { visitorId } = useParams();
  const parsedVisitorId = Number(visitorId);
  const isValid = Number.isFinite(parsedVisitorId) && parsedVisitorId > 0;

  const { user } = useSelector((state: RootState) => state.auth);
  const isClientRole = user?.role === "CLIENT" || user?.role === "CLIENT_EMPLOYEE";

  const { data: visitor } = useGetVisitorByIdQuery(parsedVisitorId, { skip: !isValid });
  const { data: allOrders = [], isLoading } = useGetDashboardOrdersQuery({
    clientId: isClientRole ? user?.clientId : undefined
  });

  // Security Check: If client role, ensure visitor belongs to their clientId
  if (isClientRole && visitor && visitor.clientId !== user?.clientId) {
    return (
      <div className="p-8 text-center text-red-600 font-bold">
        Unauthorized: You do not have access to this visitor's orders.
      </div>
    );
  }

  const orders = useMemo(
    () =>
      allOrders
        .filter((order) => order.userId === parsedVisitorId)
        .sort((a, b) => {
          const dateA = new Date(a.orderDate).getTime();
          const dateB = new Date(b.orderDate).getTime();
          if (Number.isFinite(dateA) && Number.isFinite(dateB) && dateA !== dateB) {
            return dateB - dateA;
          }
          return (b.appointmentId ?? 0) - (a.appointmentId ?? 0);
        }),
    [allOrders, parsedVisitorId]
  );

  return (
    <div className="space-y-5 p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" asChild size="sm">
            <Link to={isClientRole ? "/client/visitors" : "/super-admin/visitors"}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {visitor ? `${visitor.firstName} ${visitor.lastName}` : "Visitor"} Orders
            </h1>
            <p className="text-sm text-slate-500">
              Showing all orders for selected visitor, newest first.
            </p>
          </div>
        </div>
        <Badge variant="secondary">Total: {orders.length}</Badge>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-slate-500">Loading orders...</CardContent>
        </Card>
      ) : orders.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-slate-500">No orders found for this visitor.</CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <Card key={order.appointmentId} className="border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-base">
                    Order #{order.orderNumber || "-"} • {order.testName || "-"}
                  </CardTitle>
                  <Badge>{order.orderStatus || "-"}</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-slate-600">
                <p>
                  <strong>Client:</strong> {order.clientName || "-"}
                </p>
                <p>
                  <strong>Submitted By:</strong> {order.orderSubmittedBy || "-"}
                </p>
                <p className="inline-flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {order.clinicName || "-"} • {order.clinicAddress || "-"}
                </p>
                <p className="inline-flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Order Date: {formatDateTime(order.orderDate)} • Visit Date: {formatDateTime(order.visitDate || order.appointmentDate)}
                </p>
                <div className="pt-1">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`${isClientRole ? '/client' : '/super-admin'}/dashboard/orders/${order.appointmentId}`}>View Order Details</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
